import { useEffect, useRef, useState } from 'react';
import { Camera, X, Zap } from 'lucide-react';
import jsQR from 'jsqr';

/**
 * Full-screen camera that reads a QR code. Uses the phone's back camera; the browser's own BarcodeDetector when it has one
 * (Chrome/Android), otherwise jsQR on each frame (iPhone Safari, desktop). Calls onCode once with the text.
 */
export function QrScanner({ onCode, onClose, title = 'Scan the QR code', hint = 'Point your camera at the code on the board.' }: {
  onCode: (text: string) => void; onClose: () => void; title?: string; hint?: string;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [err, setErr] = useState('');
  const [torch, setTorch] = useState<MediaStreamTrack | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    const Detector = (window as any).BarcodeDetector;
    const detector = Detector ? new Detector({ formats: ['qr_code'] }) : null;

    const found = (text: string) => {
      if (done.current || !text) return;
      done.current = true;
      try { navigator.vibrate?.(80); } catch { /* not supported */ }
      onCode(text);
    };

    const tick = async () => {
      if (stopped || done.current) return;
      const v = video.current, c = canvas.current;
      if (v && c && v.readyState >= 2 && v.videoWidth) {
        try {
          if (detector) {
            const codes = await detector.detect(v);
            if (codes?.[0]?.rawValue) return found(codes[0].rawValue);
          } else {
            const w = Math.min(640, v.videoWidth), h = Math.round((v.videoHeight / v.videoWidth) * w);
            c.width = w; c.height = h;
            const ctx = c.getContext('2d', { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(v, 0, 0, w, h);
              const img = ctx.getImageData(0, 0, w, h);
              const code = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
              if (code?.data) return found(code.data);
            }
          }
        } catch { /* a bad frame; keep going */ }
      }
      raf = requestAnimationFrame(() => { void tick(); });
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setErr('This browser cannot open the camera. Type the code instead.'); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } }, audio: false });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = video.current!;
        v.srcObject = stream;
        v.setAttribute('playsinline', 'true');
        await v.play();
        const track = stream.getVideoTracks()[0];
        if ((track?.getCapabilities?.() as any)?.torch) setTorch(track);
        void tick();
      } catch (e: any) {
        setErr(e?.name === 'NotAllowedError' ? 'Camera permission was refused. Allow the camera for this site in your browser settings, or type the code.' : 'Could not open the camera. Type the code instead.');
      }
    })();

    return () => { stopped = true; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [onCode]);

  const toggleTorch = async () => {
    if (!torch) return;
    try { await torch.applyConstraints({ advanced: [{ torch: !torchOn } as any] }); setTorchOn(!torchOn); } catch { /* not supported */ }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div><p className="font-bold">{title}</p><p className="text-xs text-white/70">{hint}</p></div>
        <button onClick={onClose} className="p-2 rounded-full bg-white/10" aria-label="Close camera"><X className="w-5 h-5" /></button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={video} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
        <canvas ref={canvas} className="hidden" />
        {!err && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="w-64 h-64 sm:w-72 sm:h-72 rounded-3xl border-4 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] relative">
              <div className="absolute left-3 right-3 top-1/2 h-0.5 bg-green-400 animate-pulse" />
            </div>
          </div>
        )}
        {err && (
          <div className="absolute inset-0 grid place-items-center p-6">
            <div className="bg-white rounded-2xl p-5 max-w-sm text-center">
              <Camera className="w-8 h-8 text-slate-400 mx-auto" />
              <p className="text-sm text-slate-700 mt-2">{err}</p>
              <button onClick={onClose} className="mt-4 w-full py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold">Type the code</button>
            </div>
          </div>
        )}
      </div>
      {torch && !err && (
        <div className="p-4 flex justify-center">
          <button onClick={toggleTorch} className={`px-4 py-2.5 rounded-full text-sm font-bold inline-flex items-center gap-2 ${torchOn ? 'bg-amber-400 text-slate-900' : 'bg-white/10 text-white'}`}><Zap className="w-4 h-4" />{torchOn ? 'Light on' : 'Light'}</button>
        </div>
      )}
    </div>
  );
}
